/**
 * Hotel setup — the admin curates the operational facts the AI receptionist uses
 * to answer guests (check-in time, cancellation policy, breakfast hours, …).
 * Saving a fact publishes it into the AI knowledge layer and counts toward the
 * onboarding activation gate for its category.
 *
 * Facts split into **Required for activation** (the gate's four) and **Good to
 * have** (the rest). Editing requires `hotel:configure`; otherwise the page is
 * read-only. Data via `useHotelFacts` / `useUpsertHotelFact` / `useDeleteHotelFact`.
 * Follows the plain-useState pattern of `hotel/hotel-profile-page.tsx`.
 */
import {
  type HotelFact,
  type HotelFactsResponse,
  useDeleteHotelFact,
  useHotelFacts,
  useUpsertHotelFact,
} from '@hostesa/api-client';
import { useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  CardContent,
  ErrorState,
  Label,
  LoadingState,
  PageHeader,
  StatusBadge,
  Textarea,
} from '@hostesa/ui';
import { useMemo, useState } from 'react';
import { useHotelContext } from '../../lib/hotel-context';
import { useImpersonationGuard } from '../../lib/impersonation-guard';

const PAGE_WRAP = 'flex flex-col gap-6';

// Turn an unknown category key into a readable label, e.g. `wine_cellar_hours` →
// `Wine cellar hours`. Used only when the i18n bundle has no copy for a category
// the backend introduced ahead of the frontend — calling `t()` on an unregistered
// key would THROW (the i18n missingKeyHandler), crashing the whole page.
function humanizeCategory(category: string): string {
  const spaced = category.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function HotelSetupPage() {
  const { t } = useT('hotelSetup');
  const { tenantId, hotelId } = useHotelContext();
  const factsQuery = useHotelFacts(tenantId, hotelId);

  return (
    <section data-testid="hotel-setup-page" className={PAGE_WRAP}>
      <PageHeader eyebrow={t('eyebrow')} title={t('title')} description={t('description')} />
      {factsQuery.isLoading ? (
        <LoadingState data-testid="hotel-setup-loading" title={t('loading')} />
      ) : factsQuery.isError || !factsQuery.data ? (
        <ErrorState
          data-testid="hotel-setup-error"
          title={t('errorTitle')}
          description={t('errorDescription')}
        />
      ) : (
        <HotelSetupForm
          key={`${tenantId}:${hotelId}`}
          tenantId={tenantId}
          hotelId={hotelId}
          data={factsQuery.data}
        />
      )}
    </section>
  );
}

function HotelSetupForm({
  tenantId,
  hotelId,
  data,
}: {
  tenantId: string;
  hotelId: string;
  data: HotelFactsResponse;
}) {
  const { t, i18n } = useT('hotelSetup');
  const { can } = useAuth();
  const { restricted } = useImpersonationGuard();

  // Only registered categories have i18n copy. A backend deployed ahead of the
  // frontend can surface an unknown category — humanize it rather than let `t()`
  // throw on the missing key.
  const labelOf = (category: string) =>
    i18n.exists(`categories.${category}.label`, { ns: 'hotelSetup' })
      ? t(`categories.${category}.label`)
      : humanizeCategory(category);
  const descriptionOf = (category: string) =>
    i18n.exists(`categories.${category}.description`, { ns: 'hotelSetup' })
      ? t(`categories.${category}.description`)
      : '';
  const editable = can('hotel:configure');

  // Fresh on every render (drives the Set/Missing badge + the Clear affordance).
  const factByCategory = useMemo(() => {
    const map = new Map<string, HotelFact>();
    for (const f of data.facts) map.set(f.category, f);
    return map;
  }, [data.facts]);

  // Local input values, seeded once from the first data load. The form key-remounts
  // per hotel, so the seed runs once; refetches after a save update `data` (and thus
  // the badges) without clobbering in-progress edits in other rows.
  const [values, setValues] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const f of data.facts) seed[f.category] = f.value;
    return seed;
  });

  const required = data.requiredCategories;
  const goodToHave = data.allowedCategories.filter((c) => !required.includes(c));
  const allRequiredSet = required.length > 0 && required.every((c) => factByCategory.has(c));

  const setValue = (category: string, value: string) =>
    setValues((prev) => ({ ...prev, [category]: value }));

  const rowProps = (category: string) => ({
    category,
    tenantId,
    hotelId,
    label: labelOf(category),
    description: descriptionOf(category),
    serverFact: factByCategory.get(category),
    value: values[category] ?? '',
    editable,
    restricted,
    onChange: (v: string) => setValue(category, v),
    onCleared: () => setValue(category, ''),
    t,
  });

  return (
    <div data-testid="hotel-setup-form" className="flex flex-col gap-6">
      {!editable && (
        <p data-testid="hotel-setup-readonly" className="text-sm text-[var(--color-stone-mid)]">
          {t('readOnlyNote')}
        </p>
      )}
      {editable && restricted && (
        <p
          data-testid="hotel-setup-impersonation-block"
          role="note"
          className="text-sm text-[var(--color-stone-mid)]"
        >
          {t('impersonationNote')}
        </p>
      )}

      <section data-testid="hotel-setup-required" className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <h2 className="font-[var(--font-serif)] text-lg text-[var(--color-charcoal)]">
              {t('required.title')}
            </h2>
            {allRequiredSet && (
              <StatusBadge
                data-testid="hotel-setup-activation-complete"
                status="success"
                label={t('activationComplete')}
              />
            )}
          </div>
          <p className="text-sm text-[var(--color-stone-mid)]">{t('required.description')}</p>
        </div>
        <Card>
          <CardContent className="flex flex-col gap-6 pt-6">
            {required.map((category) => (
              <FactRow key={category} {...rowProps(category)} showClear={false} />
            ))}
          </CardContent>
        </Card>
      </section>

      <section data-testid="hotel-setup-good-to-have" className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <h2 className="font-[var(--font-serif)] text-lg text-[var(--color-charcoal)]">
            {t('goodToHave.title')}
          </h2>
          <p className="text-sm text-[var(--color-stone-mid)]">{t('goodToHave.description')}</p>
        </div>
        <Card>
          <CardContent className="flex flex-col gap-6 pt-6">
            {goodToHave.map((category) => (
              <FactRow key={category} {...rowProps(category)} showClear={true} />
            ))}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

interface FactRowProps {
  category: string;
  tenantId: string;
  hotelId: string;
  label: string;
  description: string;
  serverFact: HotelFact | undefined;
  value: string;
  editable: boolean;
  restricted: boolean;
  showClear: boolean;
  onChange: (value: string) => void;
  onCleared: () => void;
  t: ReturnType<typeof useT>['t'];
}

function FactRow({
  category,
  tenantId,
  hotelId,
  label,
  description,
  serverFact,
  value,
  editable,
  restricted,
  showClear,
  onChange,
  onCleared,
  t,
}: FactRowProps) {
  // Per-row mutation instances: pending/success/error state stays with THIS row
  // even when several rows save concurrently (shared instances misattributed it).
  const upsert = useUpsertHotelFact(tenantId, hotelId);
  const del = useDeleteHotelFact(tenantId, hotelId);
  const isSet = serverFact !== undefined;
  const savingThis = upsert.isPending;
  const clearingThis = del.isPending;
  const savedThis = upsert.isSuccess;
  const saveErrorThis = upsert.isError;
  const clearErrorThis = del.isError;

  const trimmed = value.trim();
  const unchanged = trimmed === (serverFact?.value ?? '');
  const busy = savingThis || clearingThis;
  const inputDisabled = !editable || restricted || busy;
  const saveDisabled = inputDisabled || unchanged || trimmed === '';

  const onSave = () => {
    if (saveDisabled) return;
    upsert.mutate({ category, value: trimmed });
  };
  const onClear = () => {
    if (inputDisabled || !isSet) return;
    del.mutate({ category }, { onSuccess: onCleared });
  };

  const inputId = `fact-${category}`;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={inputId}>{label}</Label>
        <StatusBadge
          data-testid={`fact-status-${category}`}
          status={isSet ? 'success' : 'warning'}
          label={isSet ? t('status.set') : t('status.missing')}
        />
      </div>
      {description && <p className="text-xs text-[var(--color-stone-mid)]">{description}</p>}
      <Textarea
        id={inputId}
        data-testid={`fact-input-${category}`}
        rows={2}
        value={value}
        disabled={inputDisabled}
        onChange={(e) => onChange(e.target.value)}
      />
      {editable && (
        <div className="flex items-center gap-3 pt-0.5">
          <Button
            type="button"
            size="sm"
            data-testid={`fact-save-${category}`}
            disabled={saveDisabled}
            onClick={onSave}
          >
            {savingThis ? t('saving') : t('save')}
          </Button>
          {showClear && isSet && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              data-testid={`fact-clear-${category}`}
              disabled={inputDisabled}
              onClick={onClear}
            >
              {clearingThis ? t('clearing') : t('clear')}
            </Button>
          )}
          {savedThis && (
            <span
              data-testid={`fact-saved-${category}`}
              className="text-sm text-[var(--color-success)]"
            >
              {t('saved')}
            </span>
          )}
          {saveErrorThis && (
            <span
              data-testid={`fact-error-${category}`}
              className="text-sm text-[var(--color-alert)]"
            >
              {t('saveError')}
            </span>
          )}
          {clearErrorThis && (
            <span
              data-testid={`fact-clear-error-${category}`}
              className="text-sm text-[var(--color-alert)]"
            >
              {t('clearError')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
